import {
  codeSlashOutline,
  logoGitlab,
  mail,
  personCircle
} from 'ionicons/icons';

import { GL_CLIENT_ID, OAUTH_REDIRECT_URI } from '../constants/secrets';
import { ServiceTypes } from '../constants/service';
import {
  GitlabAvatar,
  GitlabCommit,
  GitlabGroup,
  GitlabNamespace,
  GitlabProject,
  GitlabUser,
  GitlabUserEmail
} from '../types/gitlab.types';
import { Commit, Organization, User } from '../types/services.types';
import { determineDefaultAvatarForUser } from '../util/defaultAvatarUrl';
import { ServiceError } from './errors';
import { get } from './requests';
import { Service } from './service';

export class Gitlab implements Service {
  readonly id = ServiceTypes.GitLab;
  readonly host: string;
  readonly name = 'GitLab';
  readonly isCloud: boolean;

  readonly #apiUrl: string;

  readonly emailsToIgnore = ['@users.noreply.gitlab.com'];
  readonly tokenScopes = ['read_api'];
  readonly oauthScopes = ['read_api', 'profile', 'email'];
  readonly patName = 'Personal Access Token';
  readonly placeholderPat = 'glpat-xxxxxxxxxxxxxxxxxxxx';
  readonly patRegex = '^(glpat-(.*){20}|[a-zA-Z0-9_-]{20})$';
  readonly patUsername = false;
  readonly patUsernameLink = null;

  readonly orgText = 'group';
  readonly repoText = 'repository';

  readonly defaultAvatar = `/img/${this.id}.svg`;

  constructor(host: string) {
    this.host = host;
    this.isCloud = host === 'https://gitlab.com';
    this.#apiUrl = `${this.host}/api/v4`;
  }

  getOAuthLink(state: string, codeChallenge: string): string {
    return `https://gitlab.com/oauth/authorize?client_id=${GL_CLIENT_ID}&redirect_uri=${OAUTH_REDIRECT_URI}&response_type=code&state=${state}&scope=${this.oauthScopes.join('+')}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  }

  getTokenUrl(org: string, ide: string, machine?: string): string {
    let description = `GitLive | ${ide}`;
    if (machine) description += ` | ${machine}`;

    return `${this.host}/-/user_settings/personal_access_tokens?name=${encodeURIComponent(description)}&scopes=${encodeURIComponent(this.tokenScopes.join(','))}`;
  }

  async hasAccessToRepoProvided(
    token: string,
    org: string,
    repo: string | null
  ): Promise<boolean> {
    if (!repo) return true;
    try {
      await this.#get<GitlabProject>(
        token,
        `/projects/${encodeURIComponent(`${org}/${repo}`)}`
      );

      return true;
    } catch (error: any) {
      return false;
    }
  }

  async getUser(token: string, org: string): Promise<User> {
    const currentUserResult = await this.#get<GitlabUser>(token, `/user`);

    return {
      id: `${currentUserResult.id}`,
      photoURL: currentUserResult.avatar_url,
      displayName: currentUserResult.name,
      login: currentUserResult.username,
      email: currentUserResult.email,
      service: this.id
    };
  }

  async getUserEmails(token: string, org: string): Promise<string[]> {
    const userEmailsResult = await this.#get<GitlabUserEmail[]>(
      token,
      `/user/emails`
    );

    return userEmailsResult.map((it) => it.email);
  }

  async getOrganization(token: string, org: string): Promise<Organization> {
    const namespace = await this.#getNamespace(token, org);

    return {
      id: `${namespace.id}`,
      photoURL: namespace.avatar_url || this.defaultAvatar,
      displayName: namespace.name,
      type: namespace.kind,
      createdAt: namespace.created_at,
      enterprise: namespace.enterprise
    };
  }

  async getMostRecentRepos(token: string, org: string): Promise<string[]> {
    const namespace = await this.#getNamespace(token, org);
    let lastUpdatedRepos = await this.#get<GitlabProject[]>(
      token,
      `/${namespace.kind}s/${encodeURIComponent(org)}/projects?visibility=private&order_by=last_activity_at&per_page=5`
    );
    if (lastUpdatedRepos.length === 0)
      lastUpdatedRepos = await this.#get<GitlabProject[]>(
        token,
        `/${namespace.kind}s/${encodeURIComponent(org)}/projects?visibility=public&order_by=last_activity_at&per_page=5`
      );

    return lastUpdatedRepos.map((it) => it.path);
  }

  async getLatestCommitsFromRepos(
    token: string,
    org: string,
    repos: string[]
  ): Promise<Commit[][]> {
    return await Promise.all(
      repos.map(async (repo) => {
        try {
          const latestCommits = await this.#get<GitlabCommit[]>(
            token,
            `/projects/${encodeURIComponent(`${org}/${repo}`)}/repository/commits?per_page=100&page=1`
          );

          return latestCommits.map(({ author_email, author_name }) => ({
            email: author_email,
            name: author_name,
            photoURL: determineDefaultAvatarForUser(author_name, author_email)
          }));
        } catch (error: any) {
          console.warn(`Failed to fetch commits for ${org}/${repo}:`, error);

          return [];
        }
      })
    );
  }

  async getAvatar(
    token: string,
    email: string,
    placeholder: string,
    commitUrl: string
  ): Promise<string> {
    const avatarResponse = await this.#get<GitlabAvatar>(
      token,
      `/avatar?email=${email}&size=32`
    );

    return avatarResponse.avatar_url;
  }

  getNonCloudAuthenticatedUrl(url: string): { url: string; useAuth: boolean } {
    const imageUrl = new URL(url);
    const hostUrl = new URL(this.host);

    return {
      useAuth: imageUrl.hostname === hostUrl.hostname,
      url
    };
  }

  getAuthorizationHeader(token: string): string {
    return `Bearer ${token}`;
  }

  readonly oauthPermissions = [
    {
      id: 'gitlab_requests',
      icon: logoGitlab,
      title: 'Requested OAuth scopes',
      subtitle:
        'When you authorize GitLive on your GitLab account we request permission to the following resources'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'read_api',
          icon: codeSlashOutline,
          title: 'Read Api',
          subtitle:
            'Grants read access to the API, including all groups and projects.',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally.' +
            '<p style="padding-top: 4px">It is also used to display branches from private repositories in the organization view.</p>'
        },
        {
          id: 'profile',
          icon: personCircle,
          title: 'Read-only access to your personal information',
          subtitle:
            "Grants read-only access to the user's profile data using OpenID Connect.",
          descriptionHTML:
            'Your email addresses are used to link your account with commit data from your git repository.' +
            '<p style="padding-top: 4px">Additionally, your name and avatar is used to display to the other members of your organization.</p>'
        },
        {
          id: 'email',
          icon: mail,
          title: 'Read-only access to your primary email address',
          subtitle:
            "Grants read-only access to the user's primary email address using OpenID Connect.",
          descriptionHTML:
            'Your primary email address is used to identify and contact you.'
        }
      ]
    }
  ];

  readonly tokenPermissions = [
    {
      id: 'gitlab_requests',
      icon: logoGitlab,
      title: 'Required scopes',
      subtitle:
        'The following scopes are required when creating the personal access token'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'read_api',
          icon: codeSlashOutline,
          title: 'read_api',
          subtitle:
            'Grants read access to the API, including all groups and projects.',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally and to display branches from private repositories in the organization view.' +
            '<p style="padding-top: 4px">Also your name, avatar and email addresses is accessed via the API to display to the other members of your organization and link your account with commit data from your git repository.</p>'
        }
      ]
    }
  ];

  async #getNamespace(token: string, org: string): Promise<GitlabNamespace> {
    try {
      const orgResult = await this.#get<GitlabGroup>(
        token,
        `/groups/${org}?with_projects=false`
      );

      const isEnterprise = Boolean(
        orgResult.shared_runners_minutes_limit ||
          orgResult.extra_shared_runners_minutes_limit ||
          orgResult.marked_for_deletion_on ||
          orgResult.membership_lock ||
          orgResult.duo_features_enabled ||
          orgResult.lock_duo_features_enabled ||
          orgResult.duo_availability ||
          orgResult.experiment_features_enabled
      );

      return {
        id: `${orgResult.id}`,
        name: orgResult.name,
        path: orgResult.path,
        avatar_url: orgResult.avatar_url,
        kind: 'group',
        created_at: orgResult.created_at,
        enterprise: isEnterprise
      };
    } catch (error: any) {
      if (!(error instanceof ServiceError)) throw error;
    }

    try {
      const userResult = await this.#get<GitlabUser[]>(
        token,
        `/users?username=${org}`
      );
      if (userResult.length === 0) {
        throw new ServiceError(
          `No access to ${org}`,
          'Unable to find a user or group with the provided name. Please ensure you have access to the user or group and try again.'
        );
      }
      const userData = userResult[0];

      return {
        id: `${userData.id}`,
        name: userData.name,
        path: userData.username,
        avatar_url: userData.avatar_url,
        kind: 'user',
        created_at: userData.created_at,
        enterprise: false
      };
    } catch (error: any) {
      throw error;
    }
  }

  async #get<T>(oauthToken: string, uri: string): Promise<T> {
    return await get(this.#apiUrl, oauthToken, uri);
  }
}
